import React, {useContext, useMemo, useState} from "react";
import "./ContentDeclarationURSSAF.scss";
import {getIdWithToken} from "../../../../utils/storage/Token";
import FCSpinner from "../../../FCObjets/FCSpinner";
import {useFetchDeclarationUrssaf, useFetchEcheanceUrssaf} from "../../../../service/Business/DeclarationService";
import {DeclarationInterface, BillingDeclarationUrssafInterface} from "../../../../service/Business/Interfaces/DeclarationInterface";
import LineDeclarationURSSAF from "./LineDeclarationURSSAF";
import {EventUpdatedContext} from "../../../../store/EventUpdatedContext";
import FCTag from "../../../FCObjets/FCTag";
import StatusBilling from "../../../../utils/Business/StatusBilling";
import FCSortableHeader from "../../../FCObjets/FCSortableHeader";
import {useTableSort} from "../../../../hook/useTableSort";
import formatEuro from "../../../../utils/formatEuro";

type UrssafSortKey = "num" | "state" | "tot" | "begin" | "end" | "desc";

function ContentDeclarationURSSAF() {
    const [year, setYear] = useState(new Date().getFullYear());
    const {cptUpdated, setUpdated} = useContext(EventUpdatedContext);
    const {data: resumeUrssaf, loading, error} = useFetchDeclarationUrssaf(getIdWithToken(), year, cptUpdated);
    const {data: echeanceUrssaf, loading: loadingEcheance, error: errorEcheance} = useFetchEcheanceUrssaf(getIdWithToken(), year, cptUpdated);

    const [activeTab, setActiveTab] = useState<string>("all");

    const stats = useMemo(() => {
        if (!resumeUrssaf) return {totalHT: 0, nbDeclarations: 0};
        let totalHT = 0;
        let nbDeclarations = 0;
        resumeUrssaf.forEach((dec: DeclarationInterface) => {
            dec.billings.forEach((b: BillingDeclarationUrssafInterface) => {
                totalHT += b.tot;
                nbDeclarations++;
            });
        });
        return {totalHT, nbDeclarations};
    }, [resumeUrssaf]);

    const allBillings = useMemo(() => {
        if (!resumeUrssaf) return [];
        const items: {dec: DeclarationInterface; billing: BillingDeclarationUrssafInterface}[] = [];
        resumeUrssaf.forEach((dec: DeclarationInterface) => {
            dec.billings.forEach((b: BillingDeclarationUrssafInterface) => {
                items.push({dec, billing: b});
            });
        });
        return items;
    }, [resumeUrssaf]);

    type RowItem = {dec: DeclarationInterface; billing: BillingDeclarationUrssafInterface};
    const {sortedData: sortedBillings, sortState, handleSort} = useTableSort<RowItem, UrssafSortKey>(
        allBillings,
        {
            num: (r) => r.billing.num,
            state: (r) => r.billing.state,
            tot: (r) => r.billing.tot,
            begin: (r) => new Date(r.billing.begin),
            end: (r) => new Date(r.billing.end),
            desc: (r) => r.dec.desc,
        },
    );

    const isLoading = loading || loadingEcheance;
    const hasError = error || errorEcheance;

    return (
        <div className="urssafPage">
            {/* Header */}
            <div className="urssafHeader">
                <div>
                    <h1 className="urssafTitle">Déclarations URSSAF {year}</h1>
                    <p className="urssafSubtitle">Suivi des cotisations sociales et déclarations trimestrielles pour l'exercice fiscal en cours</p>
                </div>
                <div className="urssafHeaderRight">
                    <div className="urssafYearNav">
                        <button className="urssafYearBtn" onClick={() => setYear(year - 1)}>
                            <span className="material-symbols-outlined">chevron_left</span>
                        </button>
                        <span className="urssafYear">{year}</span>
                        <button className="urssafYearBtn" onClick={() => setYear(year + 1)}>
                            <span className="material-symbols-outlined">chevron_right</span>
                        </button>
                    </div>
                </div>
            </div>

            {isLoading && <div className="urssafLoading"><FCSpinner size="middle"/></div>}
            {hasError && <div className="urssafEmptyState">{error?.message || errorEcheance?.message}</div>}

            {resumeUrssaf && echeanceUrssaf && (
                <>
                    {/* KPI Cards */}
                    <div className="urssafKpiGrid">
                        <div className="urssafKpiCard urssafKpiTotal">
                            <div className="urssafKpiLabel">Total cotisations</div>
                            <div className="urssafKpiValue">{formatEuro(stats.totalHT)}</div>
                            <div className="urssafKpiSub">{stats.nbDeclarations} facture{stats.nbDeclarations > 1 ? "s" : ""} déclarée{stats.nbDeclarations > 1 ? "s" : ""}</div>
                        </div>
                        <div className="urssafKpiCard">
                            <div className="urssafKpiLabel">Statut global</div>
                            <div className="urssafKpiStatusBadge">À jour</div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="urssafTabs">
                        <button className={`urssafTab ${activeTab === "all" ? "urssafTabActive" : ""}`}
                                onClick={() => setActiveTab("all")}>
                            Toutes les déclarations
                        </button>
                    </div>

                    {/* Desktop Table */}
                    <div className="urssafTableWrapper">
                        <table className="urssafTable">
                            <thead>
                            <tr>
                                <th><FCSortableHeader label="Numéro" sortKey="num" sortState={sortState} onSort={handleSort}/></th>
                                <th><FCSortableHeader label="Statut" sortKey="state" sortState={sortState} onSort={handleSort}/></th>
                                <th><FCSortableHeader label="Tot. HT" sortKey="tot" sortState={sortState} onSort={handleSort}/></th>
                                <th><FCSortableHeader label="Début" sortKey="begin" sortState={sortState} onSort={handleSort}/></th>
                                <th><FCSortableHeader label="Fin" sortKey="end" sortState={sortState} onSort={handleSort}/></th>
                                <th><FCSortableHeader label="Trimestre" sortKey="desc" sortState={sortState} onSort={handleSort}/></th>
                            </tr>
                            </thead>
                            <tbody>
                            {sortedBillings.map(({dec, billing}, index) => (
                                <tr key={index}>
                                    <LineDeclarationURSSAF
                                        decBDD={dec}
                                        billing={billing}
                                        year={year}
                                        onUpdated={() => setUpdated()}
                                        echeancesOpen={echeanceUrssaf}
                                    />
                                </tr>
                            ))}
                            {sortedBillings.length === 0 && (
                                <tr>
                                    <td colSpan={6}>
                                        <div className="urssafEmptyState">Aucune déclaration pour {year}</div>
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                        <div className="urssafTableFooter">
                            <span className="urssafTableCount">
                                {sortedBillings.length} facture{sortedBillings.length > 1 ? "s" : ""}
                            </span>
                        </div>
                    </div>

                    {/* Mobile Cards */}
                    <div className="urssafMobileList">
                        {sortedBillings.map(({dec, billing}, index) => (
                            <div className="urssafMobileCard" key={index}>
                                <div className="urssafMobileCardHeader">
                                    <span className="urssafMobileCardTitle">Facture {billing.num}</span>
                                    <FCTag value={StatusBilling.toString(billing.state)}
                                           color={StatusBilling.toColor(billing.state)}
                                           style={{fontWeight: "bold"}}/>
                                </div>
                                <div className="urssafMobileCardGrid">
                                    <div className="urssafMobileCardItem">
                                        <span className="urssafMobileCardItemLabel">Total HT</span>
                                        <span className="urssafMobileCardItemValueGold">{formatEuro(billing.tot)}</span>
                                    </div>
                                    <div className="urssafMobileCardItem">
                                        <span className="urssafMobileCardItemLabel">Trimestre</span>
                                        <span className="urssafMobileCardItemValue">{dec.desc}</span>
                                    </div>
                                    <div className="urssafMobileCardItem">
                                        <span className="urssafMobileCardItemLabel">Début</span>
                                        <span className="urssafMobileCardItemValue">{new Date(billing.begin).toLocaleDateString()}</span>
                                    </div>
                                    <div className="urssafMobileCardItem">
                                        <span className="urssafMobileCardItemLabel">Fin</span>
                                        <span className="urssafMobileCardItemValue">{new Date(billing.end).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {sortedBillings.length === 0 && (
                            <div className="urssafEmptyState">Aucune déclaration pour {year}</div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default ContentDeclarationURSSAF;
